import React from "react";
import { InboxRow } from "../../pages/CommunicationCentrePage";

/* ─────────────────────────────────────────────────────────────
   MarketingCommunication · Phase 3 Homepage
   ------------------------------------------------------------
   Light section demonstrating that every communication is tied
   to a real transaction. Reuses the REAL production `InboxRow`
   from CommunicationCentrePage — the exact row that renders on
   the authenticated /communication-centre page.

   Story: MESSAGE → PERSON → TRANSACTION → ACTION.

   Card actions (link/dismiss/open) are intercepted at the
   wrapper level so nothing fires on the marketing page. The
   production row itself is untouched.
   ───────────────────────────────────────────────────────────── */

const isoAgo = (offsetMs) => new Date(Date.now() - offsetMs).toISOString();

/* Static demo inbox — shaped exactly like the flattened row
   the production /api/v2/mailbox/inbox endpoint returns
   (match_bucket / deal_id / deal_name at the top level, plus
   the legacy back-compat fields the InboxRow supports). */
const DEMO_MESSAGES = [
  {
    id: "mkt-msg-1",
    direction: "inbound",
    from_name: "Diane Mercer",
    from_email: "d.mercer@stratfordfreeholders.co.uk",
    subject: "23 Hallings Wharf — management pack update",
    snippet: "Apologies for the delay — the pack will be with you Friday morning at the latest…",
    received_at: isoAgo(1000 * 60 * 42), /* 42m ago */
    unread: true,
    match_bucket: "matched",
    match_confidence: 96,
    deal_id: "mkt-dr-01",
    deal_name: "23 Hallings Wharf, Stratford, London",
  },
  {
    id: "mkt-msg-2",
    direction: "inbound",
    from_name: "Marlow Legal — Enquiries",
    from_email: "team@marlowlegal.co.uk",
    subject: "Re: Enquiries raised · 14 Priory Lane",
    snippet: "Please find our responses to enquiries 1–3. Enquiry 4 is with the vendor for confirmation…",
    received_at: isoAgo(1000 * 60 * 60 * 3), /* 3h ago */
    unread: true,
    match_bucket: "matched",
    match_confidence: 94,
    deal_id: "mkt-dr-02",
    deal_name: "14 Priory Lane, Wimbledon",
  },
  {
    id: "mkt-msg-3",
    direction: "inbound",
    from_name: "Ivy Whitmore",
    from_email: "iw@bridgemortgages.co.uk",
    subject: "Mortgage offer issued — 11 Church Road",
    snippet: "Offer issued today. Valid to 15 April. Copy attached — please acknowledge…",
    received_at: isoAgo(1000 * 60 * 60 * 22), /* 22h ago */
    unread: false,
    match_bucket: "matched",
    match_confidence: 98,
    deal_id: "mkt-dr-03",
    deal_name: "11 Church Road, Barnes",
  },
  {
    id: "mkt-msg-4",
    direction: "inbound",
    from_name: "Kate Brennan",
    from_email: "kate.brennan@sourcewellsurveyors.co.uk",
    subject: "Survey booking — availability next week",
    snippet: "Happy to fit in Tues 4 Mar AM or Weds 5 Mar PM. Which suits the vendor?",
    received_at: isoAgo(1000 * 60 * 60 * 30), /* 30h ago */
    unread: false,
    match_bucket: "needs_review",
    deal_id: null,
    deal_name: null,
  },
];

const noop = () => {};

export const MarketingCommunication = () => (
  <section
    id="communication-centre"
    className="relative"
    style={{ background: "#FFFFFF" }}
    data-testid="landing-communication"
  >
    <div
      aria-hidden="true"
      className="absolute inset-x-0 top-0 h-px"
      style={{ background: "linear-gradient(90deg, transparent 0%, rgba(15,23,42,0.08) 50%, transparent 100%)" }}
    />
    <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-10 pt-16 lg:pt-24 pb-16 lg:pb-24">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)] gap-8 lg:gap-14 items-start">
        {/* Copy column */}
        <div>
          <p className="text-[10.5px] font-bold uppercase tracking-[0.22em] text-violet-700">
            Communication centre
          </p>
          <h2 className="font-display text-[32px] sm:text-[40px] lg:text-[52px] font-bold tracking-[-0.028em] text-slate-900 leading-[1.02] mt-4">
            Every conversation.<br />
            <span
              style={{
                background: "linear-gradient(90deg, #4F46E5 0%, #6D28D9 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              In the context of the deal.
            </span>
          </h2>
          <p className="text-[14px] sm:text-[15.5px] text-slate-600 leading-relaxed mt-5 max-w-[520px]">
            Keep updates, messages and transaction context together —
            so the team can see what was said, what changed and what needs to happen next.
          </p>

          <ul className="mt-8 space-y-3 max-w-[420px]" data-testid="landing-communication-legend">
            {[
              ["Message",     "Every inbound update, captured"],
              ["Person",      "Who sent it and their role"],
              ["Transaction", "Auto-matched to the right Deal Room"],
              ["Action",      "One step, one place — no digging"],
            ].map(([label, body]) => (
              <li key={label} className="flex items-start gap-3">
                <span className="mt-[3px] w-1.5 h-1.5 rounded-full bg-violet-500 shrink-0" />
                <div>
                  <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-violet-700">
                    {label}
                  </p>
                  <p className="text-[13px] text-slate-600 mt-0.5 leading-snug">{body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Real product surface — inbox rows from CommunicationCentrePage */}
        <div
          className="relative rounded-2xl bg-white ring-1 ring-slate-200/70 overflow-hidden"
          style={{ boxShadow: "0 40px 80px -32px rgba(15,23,42,0.20), 0 12px 24px -12px rgba(15,23,42,0.06)" }}
          onClickCapture={(e) => {
            /* Intercept every InboxRow interaction on the marketing page */
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {/* Browser chrome (matches Phase 1 Flow Centre framing) */}
          <div
            className="flex items-center gap-2 h-8 px-4 border-b"
            style={{
              background: "linear-gradient(180deg, #F8FAFC 0%, #FFFFFF 100%)",
              borderColor: "rgba(15,23,42,0.06)",
            }}
          >
            <span className="w-2.5 h-2.5 rounded-full bg-rose-300/80" />
            <span className="w-2.5 h-2.5 rounded-full bg-amber-300/80" />
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-300/80" />
            <span className="ml-3 text-[10.5px] font-semibold text-slate-500 tracking-wide">
              app.chainflowos.com / communication-centre
            </span>
            <span className="ml-auto text-[10px] font-semibold text-slate-400 inline-flex items-center gap-1.5">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 cf-pulse" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
              </span>
              Live
            </span>
          </div>

          {/* Inbox header row (desktop only — mobile relies on the
              scrollable rows themselves which already carry sender +
              subject + matched deal in the first visible columns). */}
          <div
            className="hidden md:grid grid-cols-[28px_minmax(0,220px)_minmax(0,1fr)_140px_84px_120px] gap-3 items-center px-4 py-3 border-b"
            style={{ background: "#F8FAFC", borderColor: "rgba(15,23,42,0.06)" }}
          >
            <span />
            <span className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-slate-500">Sender</span>
            <span className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-slate-500">Subject</span>
            <span className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-slate-500">Matched deal</span>
            <span className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-slate-500 text-right">Received</span>
            <span className="text-[9.5px] font-bold uppercase tracking-[0.14em] text-slate-500 text-right">Actions</span>
          </div>

          {/* Mobile: horizontal scroll preserves the REAL row primitive
              (it uses a 6-column grid too wide for 390px) instead of
              re-rendering a hand-built approximation.  A right-edge
              fade hints at the additional columns.  Desktop keeps the
              full width naturally. */}
          <div className="relative">
            <div className="overflow-x-auto md:overflow-visible">
              <ul
                className="p-2 md:p-3 space-y-1.5 bg-white min-w-[680px] md:min-w-0"
                data-testid="landing-communication-inbox"
              >
            {DEMO_MESSAGES.map((msg) => (
              <InboxRow
                key={msg.id}
                message={msg}
                selected={false}
                onToggleSelected={noop}
                onLink={noop}
                onDismiss={noop}
                onOpen={noop}
              />
            ))}
              </ul>
            </div>
            {/* Right-edge fade — only visible on mobile where the row
                is horizontally scrollable */}
            <div
              aria-hidden="true"
              className="md:hidden pointer-events-none absolute right-0 top-0 bottom-0 w-10"
              style={{ background: "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.95) 100%)" }}
            />
          </div>
        </div>
      </div>
    </div>
  </section>
);

export default MarketingCommunication;
